import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Users, AlertTriangle, AlertOctagon, ArrowRight, Activity,
} from "lucide-react";

const KPI_CARDS = [
  {
    key: "active_companies",
    label: "Active Companies",
    Icon: Building2,
    to: "/owner/users?tab=companies",
    accent: "#3B82F6", // blue
    description: "Onboarded organizations",
    testId: "kpi-companies",
  },
  {
    key: "total_users",
    label: "Total Users",
    Icon: Users,
    to: "/owner/users",
    accent: "#F9D71C", // safety yellow
    description: "Accounts across all companies",
    testId: "kpi-users",
  },
  {
    key: "expiring_soon",
    label: "Expiring Soon",
    Icon: AlertTriangle,
    to: "/owner/certifications?status=expiring_soon",
    accent: "#FFCC00",
    description: "Certifications due within 30 days",
    testId: "kpi-expiring",
  },
  {
    key: "expired",
    label: "Expired",
    Icon: AlertOctagon,
    to: "/owner/certifications?status=expired",
    accent: "#FF3B30",
    description: "Past-due certifications",
    testId: "kpi-expired",
  },
];

export default function OwnerDashboard() {
  const [kpis, setKpis] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [k, l] = await Promise.all([
          api.get("/owner/kpis"),
          api.get("/owner/logs"),
        ]);
        setKpis(k.data);
        setFeed(l.data.slice(0, 8));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 md:mb-10">
        <div className="inline-flex items-center gap-2 border-2 border-safety-yellow bg-safety-yellow/15 px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-[0.25em]">
          <span className="w-2 h-2 bg-safety-yellow" />
          Owner Console
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">PLATFORM OVERVIEW</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Cross-company KPIs and recent activity. Click any card to drill into a filtered list.
        </p>
      </div>

      {/* KPI Grid */}
      <div data-testid="kpi-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5 mb-10">
        {KPI_CARDS.map((c) => {
          const val = kpis?.[c.key];
          return (
            <button
              key={c.key}
              type="button"
              data-testid={c.testId}
              onClick={() => navigate(c.to)}
              className="group text-left bg-card text-card-foreground border-2 border-foreground dark:border-safety-yellow p-5 md:p-6 transition-all duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-brutalYellow active:translate-x-0 active:translate-y-0 active:shadow-none"
            >
              <div className="flex items-start justify-between mb-4">
                <span
                  className="inline-flex items-center justify-center h-12 w-12 border-2"
                  style={{ backgroundColor: c.accent, borderColor: c.accent, color: c.accent === "#F9D71C" || c.accent === "#FFCC00" ? "#000" : "#fff" }}
                >
                  <c.Icon className="w-6 h-6" strokeWidth={2.75} />
                </span>
                <ArrowRight className="w-5 h-5 text-muted-foreground transition-transform duration-150 group-hover:translate-x-1" strokeWidth={3} />
              </div>
              <div className="font-heading text-5xl md:text-6xl leading-none">
                {loading ? "—" : (val ?? 0).toLocaleString()}
              </div>
              <div className="mt-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                {c.label}
              </div>
              <div className="text-xs text-muted-foreground/80 mt-1">{c.description}</div>
            </button>
          );
        })}
      </div>

      {/* Activity feed */}
      <div className="bg-card border-2 border-foreground dark:border-safety-yellow">
        <div className="px-5 py-4 border-b-2 border-foreground/20 dark:border-safety-yellow/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-safety-yellow" strokeWidth={3} />
            <h2 className="font-heading text-lg md:text-xl">RECENT ACTIVITY</h2>
          </div>
          <button
            onClick={() => navigate("/owner/logs")}
            data-testid="view-all-logs"
            className="text-xs font-bold uppercase tracking-wider underline decoration-safety-yellow decoration-2 underline-offset-4 hover:text-safety-yellow"
          >
            View all &rarr;
          </button>
        </div>
        <ul data-testid="activity-feed">
          {loading && (
            <li className="px-5 py-6 text-sm text-muted-foreground font-bold uppercase tracking-wider">Loading…</li>
          )}
          {!loading && feed.length === 0 && (
            <li className="px-5 py-6 text-sm text-muted-foreground font-bold uppercase tracking-wider">No recent activity.</li>
          )}
          {!loading && feed.map((row) => (
            <li key={`${row.type}|${row.at || ""}|${row.actor || ""}`} className="px-5 py-3 border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0 flex items-center gap-3">
              <FeedDot type={row.type} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{row.actor}</div>
                <div className="text-xs text-muted-foreground truncate">{row.summary}</div>
              </div>
              <div className="hidden sm:block text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
                {row.at ? new Date(row.at).toLocaleString() : "—"}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FeedDot({ type }) {
  const map = {
    user_created:    { bg: "#F9D71C", fg: "#000", label: "USR" },
    cert_created:    { bg: "#34C759", fg: "#fff", label: "CRT" },
    company_created: { bg: "#3B82F6", fg: "#fff", label: "ORG" },
  };
  const c = map[type] || { bg: "#94a3b8", fg: "#fff", label: "—" };
  return (
    <span
      className="inline-flex items-center justify-center h-8 w-10 text-[10px] font-black uppercase tracking-wider border-2"
      style={{ backgroundColor: c.bg, borderColor: c.bg, color: c.fg }}
    >
      {c.label}
    </span>
  );
}
